<template>
	<div class="panel-wrapper hidden-print" data-testid="chicletPanel" v-show="chiclets.length > 0">
		<div class="container">
			<div class="row">
				<div class="col-xs-12">
					<div class="chiclets-label hidden-md-up" v-show="showFilteredByChiclets">Filtered By:</div>
					<div class="chiclets-scroller-sm">
						<div :class="{'chiclets-container': showFilteredByChiclets}">
							<span v-show="showFilteredByChiclets">
								<div class="chiclets-label hidden-sm-down">Filtered By:</div>
								<button class="chiclet" data-testid="chicletBtn" :class="{'disabled': chic.disabled}" :disabled="chic.disabled" v-for="chic in chiclets" :key="chic.value" @click="removeChiclet(chic)">
									<span class="sr-only">Remove “</span><span :id="`chicletName_${chic.name}`">{{ chic.name }}</span><span class="sr-only">” Filter</span><span class="chiclet-close"></span>
								</button>
							</span>
							<div v-show="allowFavorites" class="tooltip-wrap toggletip hidden-sm-down">
								<button v-show="savedSearchName" class="btn btn-link btn-link-simple btn-sm btn-add-favorite" @click="toggleFilteredByChiclets" aria-expanded="false">
									<span class="ico-heart-solid icon-adjust-horiz icon-adjust-vert"></span><span class="sr-only">Show</span> {{ savedSearchName }} <span class="sr-only">Schedule Filters</span>
								</button>
								<button v-show="!savedSearchName" class="btn btn-link btn-link-simple btn-sm btn-add-favorite" data-testid="largeAddScheduleToFavoritesBtn" @click="handleCreateFavorite">
									<span class="ico-heart-solid icon-adjust-horiz icon-adjust-vert"></span> Add Schedule to Favorites
								</button>
								<span v-show="!savedSearchName">
									<span data-toggletip data-char="i" data-position="bottom" data-btn-label="more about favorites">Adding a schedule to favorites provides quick access to it in the future without having to apply the filters again.</span>
								</span>
							</div>
							<button class="btn btn-link btn-link-simple btn-sm btn-clear-all hidden-sm-down" data-testid="largeClearAllChiclets" v-if="!isProspectMode" :disabled="defaultChicletsShowing" @click="clearAllChiclets">Clear All <span class="sr-only">Filters</span></button> <!-- TODO: Hide when in prospect mode? Also, don't disable home club in single select, and why is there no home club chiclet shown on prospect? -->
						</div>
						<div v-show="allowFavorites" class="tooltip-wrap toggletip hidden-md-up">
							<button v-show="savedSearchName" class="btn btn-link btn-link-simple btn-sm btn-add-favorite" @click="toggleFilteredByChiclets" aria-expanded="false">
								<span class="ico-heart-solid icon-adjust-horiz icon-adjust-vert"></span><span class="sr-only">Show</span> {{ savedSearchName }} <span class="sr-only">Schedule Filters</span>
							</button>
							<button v-show="!savedSearchName" class="btn btn-link btn-link-simple btn-sm btn-add-favorite" data-testid="smallAddScheduleToFavoritesBtn" @click="handleCreateFavorite">
								<span class="ico-heart-solid icon-adjust-horiz icon-adjust-vert"></span> Add Schedule to Favorites
							</button>
							<span v-show="!savedSearchName">
								<span data-toggletip data-char="i" data-position="bottom" data-btn-label="more about favorites">Adding a schedule to favorites provides quick access to it in the future without having to apply the filters again.</span>
							</span>
						</div>
						<button class="btn btn-link btn-link-simple btn-sm btn-clear-all hidden-md-up" data-testid="smallClearAllChiclets" v-if="!isProspectMode" :disabled="defaultChicletsShowing" @click="clearAllChiclets">Clear All</button> <!-- TODO: Hide when in prospect mode? Also why is there no home club shown on prospect? -->
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { defineComponent } from 'vue';
import _ from 'lodash';
import { StateService } from '@/services/state.service';
import { FrameworkService } from '@/services/framework.service';

export default defineComponent({
	name: 'Chiclets',
	props: {
		selected: {
			type: Array,
			default: null
		},
		savedSearchName: {
			type: String,
			default: null
		},
		savedSearchFilters: {
			type: Array,
			default: null
		},
		allowFavorites: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			isProspectMode: FrameworkService.isProspectExperience(),
			showFilteredByChiclets: true,
			chiclets: []
		};
	},
	computed: {
		defaultChicletsShowing() { // No enabled chiclets are shown
			return _.filter(this.selected, function(o) { return !o.disabled; }).length === 0;
		}
	},
	watch: {
		savedSearchName: function() {
			this.setSavedSearch();
		},
		selected: function() {
			this.createChiclets();
		},
		savedSearchFilters: function() {
			this.createChiclets();
		}
	},
	mounted() {
		this.setSavedSearch();
		this.createChiclets();
		if (this.allowFavorites) {
			$('[data-toggletip]').myltToggletip(); // Force toggletips to render
		}
	},
	methods: {
		setSavedSearch() {
			this.showFilteredByChiclets = this.savedSearchName ? false : true;
		},
		createChiclets() {
			const _chics = _.cloneDeep(this.selected);
			_.forEach(this.savedSearchFilters, item => {
				if (_.findIndex(_chics, ['value', item.value]) === -1) {
					_chics.push({
						name: item.name,
						type: item.type,
						value: item.value,
						disabled: item.type === 'location' && item.value === StateService.getLocationName()
					});
				}
			});
			this.chiclets = _chics;
		},
		removeChiclet(chiclet) {
			this.$emit('removed', Object.assign({}, chiclet));
		},
		toggleFilteredByChiclets() {
			this.showFilteredByChiclets = !this.showFilteredByChiclets;
			if (this.showFilteredByChiclets) {
				this.$emit('savedSearchRemoved');
			}
		},
		handleCreateFavorite() {
			this.$emit('createFavorite');
		},
		clearAllChiclets() {
			this.$emit('clearAllChiclets');
		}
	}
});
</script>

<style scoped lang="scss">
.tooltip-wrap {
	display: inline-block;
	&:not(:first-child) {
		margin-left: 1rem;
	}
}
::v-deep .toggletip-btn {
	vertical-align: top;
	margin: 0.375rem 0;
}
.btn-clear-all {
	float: right;
	line-height: 1;
	padding: 0.25rem 0;
	margin: 0.25rem 1rem 0.25rem 1.5rem;
	z-index: 3; // Get above .chiclets-scroller-sm:after fade-out
}
@include media-breakpoint-up(md) {
	.btn-clear-all {
		margin-right: 0;
	}
}
</style>
